/**
 * Chronotype Card — a 24h clock face rendered as paper-engraved typography.
 * Bedtime to wake-time arc is the "lit" interval; an italic Cormorant
 * "raat" sits at the center. Hour ticks every hour, labels at 00/06/12/18.
 *
 * Registers as <aramgah-chronotype-card>.
 */
import { store } from '../store.ts';
import { CHRONOTYPE_DISPLAY } from '../types.ts';
import type { Chronotype } from '../types.ts';

function clockTo(angleDeg: number, r: number): { x: number; y: number } {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: 120 + r * Math.cos(rad), y: 120 + r * Math.sin(rad) };
}

function minsToAngle(min: number): number {
  return (min / (24 * 60)) * 360;
}

function parseHHMM(s: string): number {
  const parts = s.split(':');
  const h = Number(parts[0] ?? 0);
  const m = Number(parts[1] ?? 0);
  return h * 60 + m;
}

export class AramgahChronotypeCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.chronotype, s.summary.insights.avg_bedtime, s.summary.insights.avg_wake_time);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(chronotype: Chronotype, bedtime: string, wake: string): void {
    const meta = CHRONOTYPE_DISPLAY[chronotype];
    const bedAngle = minsToAngle(parseHHMM(bedtime));
    const wakeAngle = minsToAngle(parseHHMM(wake));
    const sweep = (wakeAngle - bedAngle + 360) % 360;
    const arcR = 90;
    const startP = clockTo(bedAngle, arcR);
    const endP = clockTo(wakeAngle, arcR);
    const largeArc = sweep > 180 ? 1 : 0;

    const marks = Array.from({ length: 24 }, (_, h) => {
      const a = minsToAngle(h * 60);
      const p1 = clockTo(a, 104);
      const p2 = clockTo(a, h % 6 === 0 ? 114 : 108);
      return `<line x1="${p1.x}" y1="${p1.y}" x2="${p2.x}" y2="${p2.y}" stroke="oklch(60% 0.04 250 / 0.55)" stroke-width="${h % 6 === 0 ? 1.6 : 0.8}" />`;
    }).join('');

    const labels = ['00', '06', '12', '18'].map((label, i) => {
      const p = clockTo(i * 90, 128);
      return `<text x="${p.x}" y="${p.y}" font-family="Space Mono, monospace" font-size="9" fill="oklch(72% 0.03 250)" text-anchor="middle" dominant-baseline="middle" letter-spacing="2">${label}</text>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
  :host {
    display: block;
    border: 1px solid var(--rule);
    border-radius: 12px;
    padding: 24px 22px 28px;
    background:
      linear-gradient(165deg, oklch(18% 0.05 268 / 0.7), var(--paper-up)),
      var(--paper-up);
    position: relative;
    overflow: hidden;
    container-type: inline-size;
  }
  :host::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 70%, oklch(82% 0.04 250 / 0.05), transparent 50%);
    pointer-events: none;
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .head-aside {
    font-family: var(--display);
    font-style: italic;
    font-size: 14px;
    color: var(--ink-dim);
  }
  .wrap {
    display: grid;
    grid-template-columns: minmax(200px, 240px) 1fr;
    gap: 24px;
    align-items: center;
    position: relative;
    z-index: 1;
  }
  @container (max-width: 460px) {
    .wrap { grid-template-columns: 1fr; gap: 18px; }
  }
  svg { width: 100%; max-width: 240px; height: auto; margin-inline: auto; display: block; }
  .info { display: grid; gap: 6px; }
  .name {
    font-family: var(--display);
    font-style: italic;
    font-size: 30px;
    font-weight: 600;
    color: var(--moon-cream);
    line-height: 1.02;
    text-wrap: balance;
  }
  .tag {
    font-family: var(--display);
    font-style: italic;
    font-size: 17px;
    color: var(--ink-dim);
    text-wrap: pretty;
  }
  .times {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed var(--rule);
  }
  .times .k {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .times .v {
    font-family: var(--display);
    font-size: 24px;
    font-weight: 600;
    color: var(--moon-cream);
    font-feature-settings: 'tnum' 1, 'ss01' 1;
    margin-top: 4px;
  }
</style>
<div class="head">
  <div class="eyebrow">Chronotype · 24h clock</div>
  <div class="head-aside">paper-engraved · "kis bajay so kar uthe"</div>
</div>
<div class="wrap">
  <svg viewBox="0 0 240 240" aria-label="24-hour clock with bedtime to wake arc">
    <defs>
      <radialGradient id="dial-bg" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="oklch(22% 0.05 268)" />
        <stop offset="100%" stop-color="oklch(13% 0.04 268)" />
      </radialGradient>
      <linearGradient id="arc-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="oklch(82% 0.02 250)" />
        <stop offset="100%" stop-color="oklch(91% 0.04 80)" />
      </linearGradient>
    </defs>
    <circle cx="120" cy="120" r="85" fill="url(#dial-bg)" />
    <circle cx="120" cy="120" r="85" fill="none" stroke="oklch(82% 0.02 250 / 0.12)" stroke-width="0.6" />
    ${marks}
    ${labels}
    <path d="M ${startP.x} ${startP.y} A ${arcR} ${arcR} 0 ${largeArc} 1 ${endP.x} ${endP.y}"
      fill="none" stroke="url(#arc-grad)" stroke-width="9" stroke-linecap="round" opacity="0.92"
      style="stroke-dasharray: ${(sweep / 360 * 2 * Math.PI * arcR).toFixed(2)} 999;
             animation: arc-draw 1100ms var(--ease-suite, cubic-bezier(0.22, 1, 0.36, 1)) both;" />
    <circle cx="${startP.x}" cy="${startP.y}" r="6" fill="oklch(54% 0.14 268)" />
    <circle cx="${endP.x}" cy="${endP.y}" r="6" fill="oklch(91% 0.04 80)" />
    <text x="120" y="124" font-family="Cormorant Garamond, serif" font-style="italic" font-size="15" fill="oklch(78% 0.04 80 / 0.7)" text-anchor="middle">raat</text>
    <style>
      @keyframes arc-draw { from { stroke-dasharray: 0 9999; } }
    </style>
  </svg>
  <div class="info">
    <div class="name">${meta.name}</div>
    <div class="tag">"${meta.tagline}"</div>
    <div class="times">
      <div><div class="k">Bedtime</div><div class="v">${bedtime}</div></div>
      <div><div class="k">Wake</div><div class="v">${wake}</div></div>
    </div>
  </div>
</div>
`;
  }
}

customElements.define('aramgah-chronotype-card', AramgahChronotypeCard);
